import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14Styles,
  body18Styles,
  body28Styles,
  paragraph18Styles,
  paragraphStyles,
  smallcapsBold13Styles,
  smallcapsBold14Styles,
} from "@constants/typography"
import {
  DesktopOnly,
  MobileAndDesktopOnly,
  MobileAndTabletOnly,
  MobileOnly,
  TabletAndDesktopOnly,
  TabletOnly,
} from "@utils/MediaQuery"
import React from "react"
import styled from "styled-components"

const Content = styled.div`
  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const Decoration = styled.div`
  ${smallcapsBold14Styles}
`

const SideDecoration = styled(Decoration)`
  margin: 24px 0 64px;
  padding-right: 10px;
  text-align: right;

  @media ${breakpoints.tablet} {
    margin: 0 0 52px;
    padding-left: calc((6 / 9 + 10) / 12 * 100vw);
    padding-right: 0;
    text-align: left;
  }

  @media ${breakpoints.laptop} {
    width: calc(0.5 / 12 * 100vw);
    margin: 0;
    padding: 19px 0 0;
    text-align: left;
  }
`

const DescriptionWrapper = styled.div`
  @media ${breakpoints.laptop} {
    display: flex;
    width: calc(3 / 12 * 100vw);
    padding-right: calc(0.9 / 12 * 100vw);
    flex-direction: column;
    justify-content: center;
  }
`

const DescriptionHeader = styled.div`
  ${smallcapsBold13Styles}
  margin-bottom: 40px;
  color: ${colors.brandOrange};
`

const Description = styled.div`
  ${paragraphStyles}
  color: ${colors.mediumDarkGray};

  @media ${breakpoints.tablet} {
    ${body18Styles}
    width: calc(5 / 12 * 100vw);
    margin-bottom: 60px;
    padding-right: calc(3 / 9 / 12 * 100vw);
    color: ${colors.mediumDarkGray};
  }

  @media ${breakpoints.laptop} {
    ${paragraph18Styles}
  }

  @media ${breakpoints.desktop} {
    ${body28Styles}
  }
`

const Images = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  @media ${breakpoints.tablet} {
    padding-right: calc(6 / 9 / 12 * 100vw);
    width: calc((3 / 9 + 11) / 12 * 100vw);
    align-items: center;
    justify-content: space-between;
  }

  @media ${breakpoints.laptop} {
    width: calc(7.5 / 12 * 100vw);
    align-items: center;
  }
`

const LeftImageWrapper = styled.div`
  ${ImageHelperContainer} {
    width: calc(52vw - 10px);
    height: calc((52vw - 10px) * 1.89);

    @media ${breakpoints.tablet} {
      width: calc(5 / 12 * 100vw);
      height: calc(5 / 12 * 100vw * 1.76);
    }

    @media ${breakpoints.laptop} {
      width: calc(3.5 / 12 * 100vw);
      height: calc(3.5 / 12 * 100vw * 1.63);
    }
  }
`

const RightImageWrapper = styled.div`
  ${ImageHelperContainer} {
    width: calc(32vw - 10px);
    height: calc((32vw - 10px) * 1.21);
    margin-top: calc((32vw - 10px) * 0.56);
    margin-bottom: calc((32vw - 10px) * 0.24);

    @media ${breakpoints.tablet} {
      width: calc(5 / 12 * 100vw);
      height: calc(5 / 12 * 100vw) * 1.16);
      margin: 0;
    }

    @media ${breakpoints.laptop} {
      width: calc(3.5 / 12 * 100vw);
      height: calc(3.5 / 12 * 100vw * 1.11);
      margin: 0;
    }
  }
`

const CaptionSpacer = styled.div`
  width: 1px;
  height: 10px;
`

const CaptionWrapper = styled.div`
  display: flex;
  width: calc(32vw - 10px);
  padding-right: 20px;

  @media ${breakpoints.tablet} {
    width: calc(5 / 12 * 100vw);
    margin-top: 22px;
    padding: 0;
    align-items: center;
  }

  @media ${breakpoints.laptop} {
    width: calc(3 / 12 * 100vw);
    margin-top: 22px;
    padding: 0;
    align-items: center;
  }
`

const Caption = styled.div`
  font-family: "Avenir Roman";
  font-size: 13px;
  color: ${colors.black};
  line-height: 19px;
  opacity: 0.3;

  @media ${breakpoints.tablet} {
    ${body14Styles}
    color: ${colors.mediumGray};
    opacity: 1;
  }

  @media ${breakpoints.laptop} {
    ${body14Styles}
    color: ${colors.mediumGray};
    opacity: 1;
  }
`

const Dash = styled.div`
  width: 23px;
  height: 1px;
  margin-right: 12px;
  background-color: ${colors.mediumGray};

  @media ${breakpoints.mobileOnly} {
    display: none;
  }
`

interface ThreeColumnBodyProps {
  description: string
  descriptionHeader?: string
  leftImage: string
  leftImageCaption: string
  rightImage: string
  rightImageCaption: string
  sideDecoration?: Array<string>
}

const ThreeColumnBody: React.FC<ThreeColumnBodyProps> = ({
  description,
  descriptionHeader,
  leftImage,
  leftImageCaption,
  rightImage,
  rightImageCaption,
  sideDecoration,
}) => (
  <Content>
    <DescriptionWrapper>
      <DesktopOnly>
        {descriptionHeader && (
          <DescriptionHeader>{descriptionHeader}</DescriptionHeader>
        )}
      </DesktopOnly>
      <MobileAndDesktopOnly>
        <Description>{description}</Description>
      </MobileAndDesktopOnly>
      {sideDecoration && (
        <MobileAndTabletOnly>
          <SideDecoration>{sideDecoration.join(" ")}</SideDecoration>
        </MobileAndTabletOnly>
      )}
    </DescriptionWrapper>
    <Images>
      <LeftImageWrapper>
        <ImageHelper src={leftImage} />
        <TabletAndDesktopOnly>
          <CaptionWrapper>
            {leftImageCaption && (
              <>
                <Dash />
                <Caption>{leftImageCaption}</Caption>
              </>
            )}
          </CaptionWrapper>
        </TabletAndDesktopOnly>
      </LeftImageWrapper>
      <RightImageWrapper>
        <TabletOnly>
          <Description>{description}</Description>
        </TabletOnly>
        <ImageHelper src={rightImage} />
        <CaptionWrapper>
          {rightImageCaption && (
            <>
              <Dash />
              <Caption>{rightImageCaption}</Caption>
            </>
          )}
        </CaptionWrapper>
        <MobileOnly>
          <CaptionSpacer />
          <CaptionWrapper>
            {leftImageCaption && <Caption>{leftImageCaption}</Caption>}
          </CaptionWrapper>
        </MobileOnly>
      </RightImageWrapper>
    </Images>
    {sideDecoration && (
      <DesktopOnly>
        <SideDecoration>{sideDecoration.join(" ")}</SideDecoration>
      </DesktopOnly>
    )}
  </Content>
)

export default ThreeColumnBody
